import { useEffect, useMemo, useRef, useState } from "react";
import api, { API, formatApiErrorDetail, getToken } from "../../api";
import { AUTHOR_OPTIONS, CHANNEL_LABEL, CHECKS_CARD_SLUG, PILLARS, STATE_LABEL } from "./labels";

// LinkedIn truncates visibly in-feed at ~210 characters and rejects past ~3000.
const LINKEDIN_TRUNCATES_AT = 210;
const LINKEDIN_MAX = 3000;
// The checklist is shown where the rules are used, not only where they are read.
const CHECKLIST_STATES = ["review", "queued"];

const CHECK_GROUPS = [
  { kind: "hard", title: "Non-negotiables: a miss means it does not publish" },
  { kind: "craft", title: "Craft: a miss means a rewrite" },
  { kind: "success", title: "Success score: what we believe makes a post work" },
];

export default function ItemDrawer({ item, checks, isAdmin, onClose, onChanged,
                                     onOpenCheck }) {
  const [draft, setDraft] = useState(item);
  const [ticked, setTicked] = useState([]);
  const [note, setNote] = useState("");
  const [urls, setUrls] = useState((item.published_urls || []).join("\n"));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Synchronous re-entrancy guard for `run` below. `busy` (state) is right
  // for the visual disabled affordance, but a state update is not
  // synchronous — a second click can land before React re-renders the
  // button as disabled. `/transition` is the only writer of the audit
  // trail, so a duplicate call means a duplicate, silently-corrupt trail
  // entry. This ref is set before the first `await`, so it closes the
  // window a state flag cannot.
  const inFlight = useRef(false);

  useEffect(() => {
    setDraft(item);
    setTicked([]);
    setNote("");
    setUrls((item.published_urls || []).join("\n"));
    setError("");
  }, [item]);

  // House pattern for closing an overlay on Escape — see MainApp.jsx's
  // dashboards-dropdown handler.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const hasLinkedIn = (draft.channels || []).includes("linkedin");
  const bodyLength = (draft.body || "").length;
  const linkedInClass = useMemo(() => {
    if (bodyLength > LINKEDIN_MAX) return "over";
    if (bodyLength > LINKEDIN_TRUNCATES_AT) return "warn";
    return "";
  }, [bodyLength]);

  const toggleChannel = (c) => {
    const now = draft.channels || [];
    setDraft({ ...draft, channels: now.includes(c) ? now.filter((x) => x !== c) : [...now, c] });
  };

  const run = async (fn) => {
    // Checked and set synchronously, before any await — this is what
    // actually blocks a second click that lands inside the same tick.
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      await fn();
      await onChanged();
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const save = () => run(() => api.patch(`/marketing/items/${item.id}`, {
    title: draft.title,
    channels: draft.channels || [],
    author: draft.author,
    body: draft.body,
    athlete_name: draft.athlete_name || "",
    pillar: draft.pillar || "",
    planned_for: draft.planned_for || null,
  }));

  const move = (to) => run(async () => {
    if (to === "killed") {
      const reason = window.prompt("Why is this being killed?");
      if (!reason || !reason.trim()) return;
      await api.post(`/marketing/items/${item.id}/transition`, { to, note: reason });
      return;
    }
    const payload = { to, note: note || null, checks: ticked };
    if (to === "published") {
      payload.published_urls = urls.split("\n").map((u) => u.trim()).filter(Boolean);
    }
    await api.post(`/marketing/items/${item.id}/transition`, payload);
  });

  const remove = () => run(async () => {
    if (!window.confirm("Delete this item outright? Killing it keeps the reason on file.")) return;
    await api.delete(`/marketing/items/${item.id}`);
    onClose();
  });

  // The success score the server will store for these ticks, shown before the
  // move so nobody has to do the arithmetic. Mirrors marketing.score_checks.
  const successChecks = checks.filter((c) => c.kind === "success");
  const successTotal = successChecks.reduce((n, c) => n + (c.weight || 0), 0);
  const successTicked = successChecks.filter((c) => ticked.includes(c.id));
  const liveScore = successTicked.length && successTotal
    ? Math.round((100 * successTicked.reduce((n, c) => n + c.weight, 0)) / successTotal)
    : null;
  const showChecklist = (item.legal_transitions || []).some((s) => CHECKLIST_STATES.includes(s));

  return (
    <div className="mk-drawer-scrim" onClick={onClose} data-testid="mk-drawer-scrim">
      <aside className="mk-drawer" onClick={(e) => e.stopPropagation()} data-testid="mk-drawer">
        <div className="mk-drawer-head">
          <span className={`mk-pill state-${item.state}`}>{STATE_LABEL[item.state]}</span>
          <span className={`mk-pill author-${item.author}`}>{item.author}</span>
          {item.source === "capture" && <span className="mk-pill">from a capture</span>}
          <button type="button" className="mk-btn-quiet" onClick={onClose}>Close</button>
        </div>

        {error && <div className="mk-error" data-testid="mk-drawer-error">{error}</div>}

        <label className="mk-label">Title</label>
        <input className="mk-input" value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })} />

        <label className="mk-label">Channels</label>
        <div className="mk-chips">
          {Object.entries(CHANNEL_LABEL).map(([key, label]) => (
            <button key={key} type="button"
              className={`mk-chip ${(draft.channels || []).includes(key) ? "on" : ""}`}
              onClick={() => toggleChannel(key)}>{label}</button>
          ))}
        </div>

        <div className="mk-grid2">
          <div>
            <label className="mk-label">Athlete</label>
            <input className="mk-input" value={draft.athlete_name || ""}
              onChange={(e) => setDraft({ ...draft, athlete_name: e.target.value })} />
          </div>
          <div>
            <label className="mk-label">Author</label>
            <select className="mk-select" value={draft.author}
              onChange={(e) => setDraft({ ...draft, author: e.target.value })}>
              {AUTHOR_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="mk-label">Pillar</label>
            <input className="mk-input" list="mk-pillars" value={draft.pillar || ""}
              onChange={(e) => setDraft({ ...draft, pillar: e.target.value })} />
            <datalist id="mk-pillars">
              {PILLARS.map((p) => <option key={p} value={p} />)}
            </datalist>
          </div>
          <div>
            <label className="mk-label">Planned for</label>
            <input className="mk-input" type="date" value={draft.planned_for || ""}
              onChange={(e) => setDraft({ ...draft, planned_for: e.target.value || null })} />
          </div>
        </div>

        <label className="mk-label">
          Body
          {hasLinkedIn && (
            <span className={`mk-counter ${linkedInClass}`} data-testid="mk-linkedin-count">
              {bodyLength} / {LINKEDIN_MAX} · LinkedIn truncates at {LINKEDIN_TRUNCATES_AT}
            </span>
          )}
        </label>
        <textarea className="mk-textarea" rows={14} value={draft.body || ""}
          onChange={(e) => setDraft({ ...draft, body: e.target.value })} />

        {item.photo_ids?.length > 0 && (
          <>
            <label className="mk-label">Photos ({item.photo_ids.length})</label>
            <div className="mk-photos">
              {item.photo_ids.map((p) => (
                // GET /api/files takes the cookie OR ?token=, which is what makes
                // this work in a sandboxed preview iframe where cookies are blocked.
                <a key={p} className="mk-photo-link" target="_blank" rel="noopener noreferrer"
                  href={`${API}/files?path=${encodeURIComponent(p)}&token=${encodeURIComponent(getToken() || "")}`}>
                  {p.split("/").pop()}
                </a>
              ))}
            </div>
          </>
        )}

        <div className="mk-row-end">
          <button type="button" className="mk-btn" disabled={busy} onClick={save}>
            {busy ? "Working…" : "Save"}
          </button>
        </div>

        {showChecklist && (
          <div className="mk-checklist" data-testid="mk-checklist">
            <div className="mk-label">
              Before this moves on — each check opens its line in
              {" "}<em>How a piece gets checked</em>.
            </div>
            {CHECK_GROUPS.map((g) => {
              const rows = checks.filter((c) => c.kind === g.kind);
              if (!rows.length) return null;
              return (
                <div key={g.kind} className="mk-check-group">
                  <div className="mk-check-group-head">{g.title}</div>
                  {rows.map((c) => (
                    <div key={c.id} className="mk-check-row">
                      <label className={`mk-check ${c.kind}`}>
                        <input type="checkbox" checked={ticked.includes(c.id)}
                          onChange={(e) => setTicked(e.target.checked
                            ? [...ticked, c.id]
                            : ticked.filter((x) => x !== c.id))} />
                        <span className="mk-check-id">{c.id}</span> {c.label}
                        {c.weight ? <span className="mk-check-weight">×{c.weight}</span> : null}
                      </label>
                      {/* The rule itself, one click away. The id, not the label,
                          is the link: ticking the box and reading the rule are
                          different intents and must not share a hit target. */}
                      <button type="button" className="mk-inline-link mk-check-link"
                        title={`Read ${c.id} in the Playbook`}
                        data-testid={`mk-check-link-${c.id}`}
                        onClick={() => onOpenCheck?.(CHECKS_CARD_SLUG, c.id)}>
                        Read {c.id}
                      </button>
                    </div>
                  ))}
                </div>
              );
            })}
            {liveScore !== null && (
              <div className="mk-check-score" data-testid="mk-check-score">
                Success score if you move it now: <strong>{liveScore}</strong>/100
                {item.score != null && <> (last scored {item.score})</>}
              </div>
            )}
            <div className="mk-check-note">
              Nothing is enforced. What you tick is recorded on the trail, so an
              approval says what was actually checked.
            </div>
          </div>
        )}

        {(item.legal_transitions || []).includes("published") && (
          <>
            <label className="mk-label">Published URLs — one per line</label>
            <textarea className="mk-textarea" rows={3} value={urls}
              onChange={(e) => setUrls(e.target.value)} />
          </>
        )}

        <label className="mk-label">Note on the move</label>
        <input className="mk-input" value={note} placeholder="optional"
          onChange={(e) => setNote(e.target.value)} />

        <div className="mk-moves">
          {(item.legal_transitions || []).map((to) => (
            <button key={to} type="button" disabled={busy}
              className={`mk-btn-quiet move-${to}`} onClick={() => move(to)}
              data-testid={`mk-move-${to}`}>
              → {STATE_LABEL[to]}
            </button>
          ))}
        </div>

        <div className="mk-trail" data-testid="mk-trail">
          <div className="mk-label">Trail</div>
          {(item.trail || []).slice().reverse().map((t, i) => (
            <div className="mk-trail-row" key={i}>
              <span className="mk-trail-move">
                {t.from ? `${STATE_LABEL[t.from]} → ` : "created at "}{STATE_LABEL[t.to]}
              </span>
              <span className="mk-trail-who">
                {t.by}{t.actor_kind === "engine" ? " (engine)" : ""} · {t.at.slice(0, 16).replace("T", " ")}
              </span>
              {t.note && <div className="mk-trail-note">{t.note}</div>}
              {t.checks?.length > 0 && (
                <div className="mk-trail-checks">checked: {t.checks.join(" · ")}</div>
              )}
            </div>
          ))}
        </div>

        {isAdmin && (
          <div className="mk-row-end">
            <button type="button" className="mk-btn-quiet danger" disabled={busy} onClick={remove}>
              Delete outright
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}
